import React, { useEffect, useRef, useState } from 'react';
import styles from './LabelFilterDropdown.module.css';

export interface LabelOption {
  label: string;
  color: string;
}

interface LabelFilterDropdownProps {
  availableLabels: LabelOption[];
  selected: Record<string, boolean>;
  onChange: (selected: Record<string, boolean>) => void;
}

const LabelFilterDropdown: React.FC<LabelFilterDropdownProps> = ({ availableLabels, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const numChecked = Object.values(selected).filter(Boolean).length;

  const handleToggle = (labelName: string) => {
    const next = { ...selected, [labelName]: !selected[labelName] };
    onChange(next);
  };

  const handleClearAll = () => {
    const cleared: Record<string, boolean> = {};
    onChange(cleared);
  };

  const handleSelectAll = () => {
    const all: Record<string, boolean> = {};
    for (const l of availableLabels) {
      all[l.label] = true;
    }
    onChange(all);
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.triggerRow}>
        <button className={styles.triggerButton} onClick={() => setOpen((v) => !v)}>
          <i className="bi bi-funnel" />
          <span>Filter</span>
        </button>
        <span className={styles.badge}>
          {numChecked > 0 ? (
            <>
              <i className="bi bi-check2-square" />
              {numChecked} selected
            </>
          ) : (
            <>
              <i className="bi bi-sliders" />
              No filters
            </>
          )}
        </span>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.actionsRow}>
            <button className={styles.actionLink} onClick={handleClearAll}>
              Clear all
            </button>
            <button className={styles.actionLink} onClick={handleSelectAll}>
              Select all
            </button>
          </div>
          <div className={styles.list}>
            {availableLabels.map((l) => (
              <label key={l.label} className={styles.item}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  checked={!!selected[l.label]}
                  onChange={() => handleToggle(l.label)}
                />
                <span className={styles.colorDot} style={{ backgroundColor: l.color }} />
                <span>{l.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LabelFilterDropdown;


